import Axios, {AxiosInstance} from "axios";
import config from "./config.ts";

export interface Mail {
  id: string;
  messageId: string;
  html: string;
  headers: Record<string, string>;
  text: string;
  textAsHtml: string;
  subject: string;
  sender: Sender;
  receiver: Receiver;
  date: string;
}

export interface Sender {
  address: string;
  name: string;
}

export interface Receiver {
  address: string;
  name: string;
}

export interface IResponse {
  success: boolean,
  mails?: Array<Mail>,
  error?: string,
  domains?: Array<string>,
  token?: string,
  user?: {
    username: string;
    domain: string;
  },
  message?: string,
  announcement?: {
    id: string;
    content: string;
  } | null
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export default class MailAPI {
  private client: AxiosInstance;

  constructor() {
    this.client = Axios.create({
      baseURL: config.apiUrl
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  async login(credentials: LoginRequest): Promise<{ token: string; user: { username: string; domain: string } }> {
    try {
      const response = await this.client.request({
        method: 'POST',
        url: '/api/auth/login',
        data: credentials,
        validateStatus: () => true
      });

      const data: IResponse = response.data;

      if (data.success && data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('mail', data.user.username);
        return { token: data.token, user: data.user };
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(`Login error: ${error.message}`);
    }
  }

  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    try {
      const response = await this.client.request({
        method: 'POST',
        url: '/api/auth/change-password',
        data: passwordData,
        validateStatus: () => true
      });

      const data: IResponse = response.data;

      if (!data.success || data.error) {
        throw new Error(data.error || 'Password change failed');
      }
    } catch (error: any) {
      throw new Error(`Change password error: ${error.message}`);
    }
  }

  async getInbox(): Promise<Mail[]> {
    try {
      const response = await this.client.request({
        method: 'GET',
        url: '/api/inbox',
        validateStatus: () => true
      });

      const data: IResponse = response.data;

      if (data.success && data.mails) {
        return data.mails;
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('mail');
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
      throw new Error(`Get inbox error: ${error.message}`);
    }
  }

  async deleteMails(mailId?: string): Promise<void> {
    try {
      const response = await this.client.request({
        method: 'DELETE',
        url: mailId ? `/api/inbox/${mailId}` : '/api/inbox',
        validateStatus: () => true
      });

      const data: IResponse = response.data;

      if (!data.success || data.error) {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('mail');
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
      throw new Error(`Delete mails error: ${error.message}`);
    }
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('mail');
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  async getAnnouncement(): Promise<{ id: string; content: string } | null> {
    try {
      const response = await this.client.request({
        method: 'GET',
        url: '/api/announcement',
        validateStatus: () => true
      });

      const data: IResponse = response.data;

      if (data.success && data.announcement) {
        return data.announcement;
      }
      return null;
    } catch (error: any) {
      console.error('Failed to fetch announcement:', error.message);
      return null;
    }
  }
}